// @flow

import * as React from 'react';
import { connect } from 'react-redux';
import { filter, map, flatMap, flow, uniqBy } from 'lodash/fp';
import Helmet from 'react-helmet';
import styled from '@emotion/styled';
import { withRouter } from 'react-router-dom';
import { withStateHandlers, compose } from 'recompose';
import pluralize from 'pluralize';
import t from 'app/utils/translate';
import * as myTeamsActions from 'app/actions/myTeamsActions';
import { FAVORITE } from 'app/constants/lineupTypeFilters';
import { ACTIVE, RUNNING } from 'app/constants/lineupStatus';
import Clickable from 'app/components/commons/Clickable';
import Tooltip from 'app/components/commons/Tooltip';
import RadioButton from 'app/components/commons/RadioButton';
import FlexiSelect from 'app/components/commons/FlexiSelect';
import SvgButton from 'app/components/commons/SvgButton';
import Icon from '../../components/commons/Icon';
import Title from '../../components/commons/Title';
import Loader from '../../components/commons/Loader';
import Modal from '../../components/commons/Modal';
import SearchBar from '../../components/commons/SearchBar';
import Responsive from '../../components/responsive/Responsive';
import RewriteLink from '../../components/routing/RewriteLink';
import { hover } from '../../utils/cssUtils';
import { rewritePath } from '../../utils/routeUtils';
import ConfirmModal from './ConfirmModal';
import LineUpGrid from './LineUpGrid';

type Props = {
  activeSport?: string,
  clearFavorites: () => void,
  darkMode: boolean,
  favoritesCleared: boolean,
  getMyLineUps: () => void,
  isClearing?: boolean,
  isLive?: boolean,
  isLoading: boolean,
  lineupsDeleted: boolean,
  lineups: Array<Object>,
  lineupTypeFilters: Array<string>,
  onConfirmDelete?: () => void,
  onFavoritesFilterChange: () => void,
  resetMyTeams: () => void,
  renderEntity: (entity: Object, positionId: string) => React.Node,
  renderBottomButtons?: (selectionHash: string, lineupId: number) => void,
  rewriteRules: Object,
  selectedLineUp?: Object,
  setSelectionHash: (selectionHash: string) => void,
  status: ACTIVE | RUNNING,
  toggleLineupFavorite: (id: number, favorite: boolean) => void
};

type State = {
  confirmClear?: boolean,
  searchTerm?: string,
  selectedSlateId?: number
};

const CenteredMessage = styled.div(
  {
    textAlign: 'center',
    textTransform: 'uppercase',
    padding: '1em'
  },
  ({ darkMode, theme }) => ({
    color: darkMode ? theme.color.basis5 : theme.color.invertableBasis4
  })
);

const Toolbar = styled.div(({ theme }) => ({
  backgroundColor: theme.color.panel,
  color: theme.color.panelAccent,
  display: 'flex',
  alignItems: 'center',
  marginBottom: theme.spacing.xsmall,
  [theme.mediaQuery.mobileOnly]: {
    flexWrap: 'wrap'
  }
}));

const FilterItem = styled.div(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  flexBasis: '230px',
  fontSize: '0.875rem',
  height: '35px',
  padding: `0 ${theme.spacing.xsmall}px`,
  textTransform: 'uppercase',
  [theme.mediaQuery.mobileOnly]: {
    flexBasis: '50%',
    marginBottom: 0
  }
}));

const LineupStats = styled(FilterItem)(({ theme }) => ({
  justifyContent: 'flex-end',
  '& :not(:last-child)': {
    marginRight: theme.spacing.xsmall
  }
}));

const FavoriteFilter = styled(FilterItem)(({ theme }) => ({
  justifyContent: 'space-between',
  [theme.mediaQuery.mobileOnly]: {
    flexBasis: '50%'
  }
}));

const SlateSelect = styled(FlexiSelect)(({ theme }) => ({
  width: '100%',
  '& div': {
    backgroundColor: `${theme.color.panel}`,
    color: theme.color.panelAccent
  }
}));

const StyledSearchBar = styled(SearchBar)(({ theme }) => ({
  fontSize: theme.fontSize.small,
  height: '35px',
  flexBasis: '50%',
  flexGrow: 1,
  [theme.mediaQuery.mobileOnly]: {
    flexBasis: '50%',
    marginBottom: 0
  }
}));

const List = styled.div({
  display: 'flex',
  flexDirection: 'column'
});

const StyledLink = styled(RewriteLink)(({ theme }) => ({
  color: 'black',
  padding: '0.25em',
  backgroundColor: 'white',
  ...hover({
    backgroundColor: theme.color.basis6
  }),
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%'
}));

const Label = styled.span({
  whiteSpace: 'nowrap'
});

const Stats = styled.div(({ kind, theme }) => ({
  display: 'flex',
  whiteSpace: 'nowrap',
  alignItems: 'center',
  height: '35px',
  marginBottom: kind === 'success' ? theme.spacing.small : 0,
  '&::before': {
    content: '"\\f120"',
    color: theme.color.success,
    display: 'inline-block',
    fontFamily: 'Ionicons',
    fontSize: 19,
    paddingRight: theme.spacing.xsmall
  }
}));

const RemoveButton = styled(SvgButton)(({ theme }) => ({
  width: '26px',
  height: '26px',
  borderRadius: '50%',
  padding: 5,
  color: theme.color.white,
  backgroundColor: theme.color.basis4
}));

const ClearButton = styled(SvgButton)(({ theme }) => ({
  color: theme.color.basis4
}));

class MyTeamsContent extends React.Component<Props> {
  props: Props;

  state: State = {
    confirmClear: false,
    expandedTeams: [],
    searchTerm: undefined,
    selectedSlateId: undefined
  };

  static defaultProps = {
    activeSport: '',
    isClearing: false,
    isLive: false,
    onConfirmDelete: undefined,
    renderBottomButtons: undefined,
    selectedLineUp: undefined
  };

  componentDidMount() {
    const { getMyLineUps, status } = this.props;
    getMyLineUps({ status });
  }

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    const { favoritesCleared, lineupsDeleted } = this.props;

    if (
      (!lineupsDeleted && nextProps.lineupsDeleted) ||
      (!favoritesCleared && nextProps.favoritesCleared)
    ) {
      const { getMyLineUps, status } = this.props;
      getMyLineUps({ status });
    }
  }

  componentWillUnmount() {
    const { resetMyTeams } = this.props;
    resetMyTeams();
  }

  handleSlateChange = selectedSlateId => this.setState({ selectedSlateId });

  handleConfirmClear = () => this.setState({ confirmClear: true });

  handleCancelClear = () => this.setState({ confirmClear: false });

  handleClear = () => {
    const { clearFavorites } = this.props;
    const { confirmClear } = this.state;

    if (confirmClear) {
      clearFavorites();
    }
    this.setState({ confirmClear: false });
  };

  render() {
    const {
      activeSport,
      darkMode,
      isClearing,
      isLive,
      isLoading,
      lineups,
      lineupTypeFilters,
      onConfirmDelete,
      onFavoritesFilterChange,
      renderBottomButtons,
      renderEntity,
      rewriteRules,
      setSelectionHash,
      selectedLineUp,
      toggleLineupFavorite
    } = this.props;

    const { confirmClear, expandedTeams, searchTerm, selectedSlateId } = this.state;
    const lowerSearchTerm = searchTerm ? searchTerm.toLowerCase().trim() : '';
    const searchTermFilteredLineups = searchTerm
      ? flow(
          filter(lineup => {
            const players = flow(
              flatMap('entities'),
              map(p => p.name.toLowerCase())
            )(lineup.selection);
            return players.find(p => p.indexOf(lowerSearchTerm) !== -1);
          })
        )(lineups)
      : lineups;

    const filteredLineups = selectedSlateId
      ? searchTermFilteredLineups.filter(l => l.slateId === selectedSlateId)
      : searchTermFilteredLineups;

    const numLineupsWithoutEntry = lineups.filter(l => l.entryCount === 0).length;
    const slateOptions = [
      {
        value: undefined,
        label: 'All Slates'
      }
    ].concat(
      uniqBy(
        'value',
        lineups.map(l => ({
          value: l.slateId,
          label: l.slateName
        }))
      )
    );

    return (
      <Responsive desktop>
        {isDesktop => (
          <div>
            <Helmet>{t('My Teams')}</Helmet>
            <Toolbar>
              <FavoriteFilter>
                <Clickable onClick={onFavoritesFilterChange}>
                  <RadioButton checked={lineupTypeFilters.includes(FAVORITE)} />
                  <Label>{'\u00a0\u00a0'}favourites Only</Label>
                </Clickable>
                <ClearButton type="icon-cross-circle" size={22} onClick={this.handleConfirmClear} />
              </FavoriteFilter>
              <FilterItem>
                <SlateSelect
                  load={false}
                  onChange={this.handleSlateChange}
                  options={slateOptions}
                  placeholder="Filter by Slate"
                  value={selectedSlateId}
                />
              </FilterItem>
              <StyledSearchBar
                placeholder="Player Search"
                onClear={() => {
                  this.setState({ searchTerm: '' });
                }}
                onSearch={value => {
                  this.setState({ searchTerm: value });
                }}
                value={searchTerm}
              />
              <LineupStats>
                <Stats>
                  {isLive
                    ? `${lineups.length} ${pluralize('lineup', lineups.length)}`
                    : `${numLineupsWithoutEntry} / ${lineups.length} ${pluralize(
                        'lineup',
                        lineups.length
                      )}`}
                </Stats>
                {numLineupsWithoutEntry > 0 && (
                  <Tooltip title="Delete all lineups with no entries">
                    <RemoveButton type="icon-bin" onClick={onConfirmDelete} />
                  </Tooltip>
                )}
              </LineupStats>
            </Toolbar>
            {filteredLineups.length === 0 &&
              (isLoading ? (
                <Loader />
              ) : (
                <CenteredMessage darkMode={darkMode}>
                  {`you have no ${isLive ? 'Live' : 'Upcoming'} ${activeSport} teams`}
                </CenteredMessage>
              ))}
            <LineUpGrid
              expandedTeams={expandedTeams}
              isLive={isLive}
              lineUps={filteredLineups}
              isDesktop={isDesktop}
              searchTerm={searchTerm}
              renderBottomButtons={renderBottomButtons}
              renderEntity={renderEntity}
              toggleLineupFavorite={toggleLineupFavorite}
            />
            {selectedLineUp &&
              Array.isArray(selectedLineUp.challenges) &&
              selectedLineUp.challenges.length > 1 && (
                <Modal
                  isOpen
                  size="small"
                  onCancel={() => {
                    setSelectionHash(null);
                  }}
                >
                  <Title>Go to {t('challenge')}</Title>
                  <List>
                    {selectedLineUp.challenges.map(challenge => (
                      <StyledLink
                        key={`link_${challenge.id}`}
                        to={rewritePath(rewriteRules)(`/challenge/${challenge.id}`)}
                      >
                        {challenge.name}
                        <Icon type="icon-right-arrow" />
                      </StyledLink>
                    ))}
                  </List>
                </Modal>
              )}
            {(confirmClear || isClearing) && (
              <ConfirmModal
                isLoading={isClearing}
                onCancel={this.handleCancelClear}
                onSubmit={this.handleClear}
                title="Clear all favourites?"
                label="Clear"
              />
            )}
          </div>
        )}
      </Responsive>
    );
  }
}

function mapStateToProps(state, { lineups, selectedSelectionHash }) {
  return {
    darkMode: state.settings.darkMode,
    lineupTypeFilters: state.myTeams.lineupTypeFilters,
    isLoading: state.myTeams.isLoading,
    isClearing: state.myTeams.isClearing,
    favoritesCleared: state.myTeams.favoritesCleared,
    lineupsDeleted: state.myTeams.lineupsDeleted,
    rewriteRules: state.settings.rewriteRules,
    selectedLineUp: lineups.find(lineup => lineup.selectionHash === selectedSelectionHash)
  };
}

const actions = {
  clearFavorites: myTeamsActions.clearFavorites,
  getMyLineUps: myTeamsActions.getMyLineUps,
  resetMyTeams: myTeamsActions.resetMyTeams,
  onFavoritesFilterChange: () => myTeamsActions.setLineupTypeFilter(FAVORITE),
  toggleLineupFavorite: myTeamsActions.toggleLineupFavorite
};

export default compose(
  withRouter,
  withStateHandlers(
    {
      selectedSelectionHash: null
    },
    {
      setSelectionHash: () => selectedSelectionHash => ({ selectedSelectionHash })
    }
  ),
  connect(mapStateToProps, actions)
)(MyTeamsContent);
